/**
 * Global Data Context for Resumeslip React App
 * Manages API data fetching, caching, and state for all pages
 */

import React, { createContext, useContext, useState, useEffect, useCallback, useRef, useMemo } from 'react';
import PropTypes from 'prop-types';
import { 
  pageApi, 
  blogApi, 
  templateApi, 
  professionApi, 
    templatePageApi,
  siteApi, 
  privacyApi,
  apiHealthCheck,
  useApi as useApiService,
  clearAllCaches
} from '../services/api.js';
import config, { log, getMediaUrl } from '../utils/environment.js';

// Create Data Context
const DataContext = createContext({});

// Custom hook to use the data context
export const useData = () => {
  const context = useContext(DataContext);
  if (!context) {
    throw new Error('useData must be used within DataProvider');
  }
  return context;
};

// Fallback data generator
const getFallbackData = (key) => {
  const fallbacks = {
      templatePage: {  // ADDED
        title: "Professional Templates",
        description: "Browse our collection of professional resume, CV, and slip templates.",
        meta_title: "Templates - Resumeslip",
        meta_description: "Browse our collection of professional resume, CV, and slip templates."
      },
    homePage: {
      hero_title: "Build Your Resume & Slip — Free and Effortless",
      hero_subtitle: "Create professional resumes and slips in minutes. No design skills needed.",
      hero_button_text: "Start Building Now →",
      features: [
        { id: 1, icon: "FaFileLines", title: "Professional Templates", description: "Choose from beautifully designed resume templates.", order: 0 },
        { id: 2, icon: "FaBolt", title: "Lightning Fast", description: "Build documents in minutes, not hours.", order: 1 },
      ],
      stats: [
        { id: 1, value: "100%", label: "Free Forever", order: 0 },
        { id: 2, value: "5 Min", label: "Average Time", order: 1 },
      ],
      steps: [
        { id: 1, number: "1", title: "Select Template", description: "Pick from professional templates.", order: 0 },
        { id: 2, number: "2", title: "Add Details", description: "Enter your information.", order: 1 },
      ],
      testimonials: [],
      faqs: [],
    },
    aboutPage: {
      about_title: "About Resumeslip",
      about_subtitle: "We help professionals create amazing resumes",
      blocks: [
        { id: 1, title: "Our Mission", content: "To make resume building accessible.", order: 0 },
        { id: 2, title: "Our Story", content: "Founded to solve resume problems.", order: 1 },
      ],
      values: [
        { id: 1, icon: "FaShieldAlt", title: "Security First", description: "Your privacy is important.", order: 0 },
        { id: 2, icon: "FaBolt", title: "Speed", description: "Fast and efficient.", order: 1 },
      ],
    },
    contactPage: {
      title: "Contact Us",
      subtitle: "We'd love to hear from you!",
      form_title: "Send Us a Message",
      email: "support@resumeslip.com",
      phone: "+1 (555) 123-4567",
      address: "123 Business Street, City, Country",
    },
    professionPage: {
      title: "Build Resumes for Every Profession",
      subtitle: "Choose your Category and start Building today",
    },
    privacyPage: {
      title: "Privacy Policy",
      content: "<h2>Our Commitment to Your Privacy</h2><p>At Resumeslip, we are committed to protecting your privacy and ensuring the security of your personal information.</p>",
      last_updated: new Date().toISOString(),
    },
    siteConfig: {
      site_name: "Resumeslip",
      default_meta_title: "Resumeslip - Build Professional Resumes & Slips",
      default_meta_description: "Create professional resumes and slips for free.",
      contact_email: "contact@resumeslip.com",
      footer_text: "All Copyright by Resumeslip",
    },
    blogArticles: [],
    blogCategories: [
      { id: 1, name: "Career Tips", slug: "career-tips", order: 0, is_active: true },
      { id: 2, name: "Technology", slug: "technology", order: 1, is_active: true },
    ],
    templates: [],
    professionCategories: [],
  };
  
  
  return fallbacks[key] || (key.includes('Array') ? [] : null);
};

// Process API responses
const processApiResponse = (response, key) => {
  if (!response) {
    log.warn(`Empty response for ${key}`);
    return getFallbackData(key);
  }

  if (Array.isArray(response)) {
    return response;
  }
  
  if (typeof response === 'object') {
    if (response.results && Array.isArray(response.results)) {
      return response.results;
    }
    
    if (response.id || response.title || response.site_name) {
      return response;
    }
    
    if (response.data) {
      return response.data;
    }
    
    // Try to extract values from object
    if (Object.keys(response).length > 0) {
      return response;
    }
    
    log.warn(`Unexpected response format for ${key}:`, response);
    return getFallbackData(key);
  }
  
  return getFallbackData(key);
};

// Data Provider Component with debug logging
export const DataProvider = ({ children }) => {
  // State for all data
  const [data, setData] = useState({
    homePage: null,
    aboutPage: null,
    contactPage: null,
    professionPage: null,
    privacyPage: null,
    templatePage: null, 
    siteConfig: null,
    blogArticles: [],
    blogCategories: [],
    templates: [],
    professionCategories: [],
  });
  
  // Loading states
  const [loading, setLoading] = useState({
    homePage: false,
    aboutPage: false,
    contactPage: false,
    professionPage: false,
    privacyPage: false,
    siteConfig: false,
    templatePage: false, 
    blogArticles: false,
    blogCategories: false,
    templates: false,
    professionCategories: false,
  });
  
  // Error states
  const [errors, setErrors] = useState({});
  
  // API connection status
  const [apiConnected, setApiConnected] = useState(null); // Start as null to indicate not checked yet
  
  // Ref to track if initial fetch has been done
  const initialFetchDone = useRef(false);
  
  // Get API service hook
  const { fetchWithCache, invalidateCache } = useApiService();
  
  // Check API connection on mount
  useEffect(() => {
    const checkConnection = async () => {
      try {
        log.info('Checking API connection...');
        const isConnected = await apiHealthCheck();
        setApiConnected(isConnected);
        log.info(`API Connection: ${isConnected ? 'Connected' : 'Disconnected'}`);
      } catch (error) {
        log.error('API Health Check failed:', error);
        setApiConnected(false);
      }
    };
    
    checkConnection();
    
    const interval = setInterval(checkConnection, 30000);
    return () => clearInterval(interval);
  }, []);
  
  // Generic fetch function with caching and debug logging
  const fetchData = useCallback(async (key, apiFunction, cacheKey = null, forceRefresh = false) => {
    log.info(`Fetching ${key}...`);
    
    if (apiConnected === false) {
      log.warn(`API not connected, using fallback data for ${key}`);
      const fallback = getFallbackData(key);
      setData(prev => ({ ...prev, [key]: fallback }));
      return fallback;
    }
    
    try {
      setLoading(prev => ({ ...prev, [key]: true }));
      setErrors(prev => ({ ...prev, [key]: null }));
      
      const cacheKeyToUse = cacheKey || key;
      const result = await fetchWithCache(cacheKeyToUse, apiFunction, forceRefresh);
      
      log.info(`API Response for ${key}:`, result);
      
      const processedData = processApiResponse(result, key);
      
      setData(prev => ({ ...prev, [key]: processedData }));
      log.info(`✅ Successfully fetched ${key}:`, Array.isArray(processedData) ? `${processedData.length} items` : 'Object');
      
      return processedData;
    } catch (error) {
      log.error(`❌ Error fetching ${key}:`, error);
      setErrors(prev => ({ 
        ...prev, 
        [key]: error.message || `Failed to load ${key}` 
      }));
      
      // Return fallback data
      const fallback = getFallbackData(key);
      setData(prev => ({ ...prev, [key]: fallback }));
      return fallback;
    } finally {
      setLoading(prev => ({ ...prev, [key]: false }));
    }
  }, [apiConnected, fetchWithCache]);
  
  // Individual fetch functions
  const fetchHomePage = useCallback((forceRefresh = false) => 
    fetchData('homePage', pageApi.getHomePage, 'homepage', forceRefresh), 
  [fetchData]);
  
  const fetchTemplatePage = useCallback((forceRefresh = false) => 
    fetchData('templatePage', templatePageApi.getTemplatePage, 'templatePage', forceRefresh), 
  [fetchData]);

  const fetchAboutPage = useCallback((forceRefresh = false) => 
    fetchData('aboutPage', pageApi.getAboutPage, 'aboutpage', forceRefresh), 
  [fetchData]);
  
  const fetchContactPage = useCallback((forceRefresh = false) => 
    fetchData('contactPage', pageApi.getContactPage, 'contactpage', forceRefresh), 
  [fetchData]);
  
  const fetchProfessionPage = useCallback((forceRefresh = false) => 
    fetchData('professionPage', pageApi.getProfessionPage, 'professionpage', forceRefresh), 
  [fetchData]);
  
  const fetchPrivacyPage = useCallback((forceRefresh = false) => 
    fetchData('privacyPage', privacyApi.getPrivacyPolicy, 'privacyPage', forceRefresh), 
  [fetchData]);
  
  const fetchSiteConfig = useCallback((forceRefresh = false) => 
    fetchData('siteConfig', siteApi.getConfig, 'siteconfig', forceRefresh), 
  [fetchData]);
  
  const fetchBlogArticles = useCallback((params = {}, forceRefresh = false) => {
    const cacheKey = `blogArticles_${JSON.stringify(params)}`;
    return fetchData('blogArticles', () => blogApi.getArticles(params), cacheKey, forceRefresh);
  }, [fetchData]);
  
  const fetchBlogCategories = useCallback((forceRefresh = false) => 
    fetchData('blogCategories', blogApi.getCategories, 'blogCategories', forceRefresh), 
  [fetchData]);
  
  const fetchTemplates = useCallback((params = {}, forceRefresh = false) => {
    const cacheKey = `templates_${JSON.stringify(params)}`;
    return fetchData('templates', () => templateApi.getTemplates(params), cacheKey, forceRefresh);
  }, [fetchData]);
  
  const fetchProfessionCategories = useCallback((forceRefresh = false) => 
    fetchData('professionCategories', professionApi.getCategories, 'professionCategories', forceRefresh), 
  [fetchData]);
  
  // Special blog fetching functions
  const fetchFeaturedArticles = useCallback(async (forceRefresh = false) => {
    const cacheKey = 'featuredArticles';
    try {
      const result = await fetchWithCache(cacheKey, blogApi.getFeaturedArticles, forceRefresh);
      return Array.isArray(result) ? result : (result?.results || []);
    } catch (error) {
      log.error('Error fetching featured articles:', error);
      return [];
    }
  }, [fetchWithCache]);
  
  const fetchTrendingArticles = useCallback(async (forceRefresh = false) => {
    const cacheKey = 'trendingArticles';
    try {
      const result = await fetchWithCache(cacheKey, blogApi.getTrendingArticles, forceRefresh);
      return Array.isArray(result) ? result : (result?.results || []);
    } catch (error) {
      log.error('Error fetching trending articles:', error);
      return [];
    }
  }, [fetchWithCache]);
  
  const fetchLatestArticles = useCallback(async (forceRefresh = false) => {
    const cacheKey = 'latestArticles';
    try {
      const result = await fetchWithCache(cacheKey, blogApi.getLatestArticles, forceRefresh);
      return Array.isArray(result) ? result : (result?.results || []);
    } catch (error) {
      log.error('Error fetching latest articles:', error);
      return [];
    }
  }, [fetchWithCache]);
  
  const fetchBlogArticleBySlug = useCallback(async (slug, forceRefresh = false) => {
    const cacheKey = `article_${slug}`;
    try {
      const result = await fetchWithCache(cacheKey, () => blogApi.getArticleBySlug(slug), forceRefresh);
      return result;
    } catch (error) {
      log.error(`Error fetching article ${slug}:`, error);
      return null;
    }
  }, [fetchWithCache]);
  
  const fetchTemplateBySlug = useCallback(async (slug, forceRefresh = false) => {
    const cacheKey = `template_${slug}`;
    try {
      const result = await fetchWithCache(cacheKey, () => templateApi.getTemplateBySlug(slug), forceRefresh);
      return result;
    } catch (error) {
      log.error(`Error fetching template ${slug}:`, error);
      return null;
    }
  }, [fetchWithCache]);
  
  // Initial data fetching - only once
  useEffect(() => {
    if (apiConnected === null) {
      log.info('Waiting for API connection check...');
      return;
    }
    
    if (!initialFetchDone.current) {
      initialFetchDone.current = true;
      log.info('🚀 Starting initial data fetch. API connected:', apiConnected);

      setTimeout(() => {
        fetchTemplatePage().then(templatePageData => {
          log.info('Template page loaded:', templatePageData);
        });
      }, 350);
      
      // Fetch critical data first
      fetchSiteConfig().then(config => {
        log.info('Site config loaded:', config);
      });
      
      fetchHomePage().then(homeData => {
        log.info('Home page loaded:', homeData);
      });
      
      // Then fetch other data with slight delays to prevent overwhelming
      setTimeout(() => {
        fetchBlogArticles().then(articles => {
          log.info('Blog articles loaded:', articles?.length || 0, 'articles');
        });
        
        fetchBlogCategories().then(categories => {
          log.info('Blog categories loaded:', categories?.length || 0, 'categories');
        });
      }, 100);
      
      setTimeout(() => {
        fetchAboutPage().then(aboutData => {
          log.info('About page loaded:', aboutData);
        });
        
        fetchContactPage().then(contactData => {
          log.info('Contact page loaded:', contactData);
        });
        
        fetchProfessionPage().then(professionData => {
          log.info('Profession page loaded:', professionData);
        });
      }, 200);
      
      setTimeout(() => {
        fetchTemplates().then(templates => {
          log.info('Templates loaded:', templates?.length || 0, 'templates');
        });
        
        fetchProfessionCategories().then(categories => {
          log.info('Profession categories loaded:', categories?.length || 0, 'categories');
        });
      }, 300);
      
      setTimeout(() => {
        fetchPrivacyPage().then(privacyData => {
          log.info('Privacy page loaded:', privacyData);
        });
      }, 400);
    }
  }, [
    apiConnected,
    fetchTemplatePage,
    fetchHomePage,
    fetchAboutPage,
    fetchContactPage,
    fetchProfessionPage,
    fetchPrivacyPage,
    fetchSiteConfig,
    fetchBlogArticles,
    fetchBlogCategories,
    fetchTemplates,
    fetchProfessionCategories,
  ]);
  
  // Log data changes for debugging
  useEffect(() => {
    if (Object.keys(data).some(key => data[key] !== null)) {
      log.info('Current data state:', {
        homePage: data.homePage ? 'Loaded' : 'Not loaded',
        aboutPage: data.aboutPage ? 'Loaded' : 'Not loaded',
        contactPage: data.contactPage ? 'Loaded' : 'Not loaded',
        professionPage: data.professionPage ? 'Loaded' : 'Not loaded',
        privacyPage: data.privacyPage ? 'Loaded' : 'Not loaded',
        siteConfig: data.siteConfig ? 'Loaded' : 'Not loaded',
        blogArticles: data.blogArticles.length > 0 ? `${data.blogArticles.length} articles` : 'No articles',
        blogCategories: data.blogCategories.length > 0 ? `${data.blogCategories.length} categories` : 'No categories',
      });
    }
  }, [data]);
  
  // Refresh specific data
  const refreshData = useCallback((dataType, forceRefresh = false) => {
    log.info(`🔄 Refreshing ${dataType}...`);
    
    const refreshMap = {
      homePage: () => fetchHomePage(forceRefresh),
      aboutPage: () => fetchAboutPage(forceRefresh),
      contactPage: () => fetchContactPage(forceRefresh),
      professionPage: () => fetchProfessionPage(forceRefresh),
      privacyPage: () => fetchPrivacyPage(forceRefresh),
      siteConfig: () => fetchSiteConfig(forceRefresh),
      blogArticles: () => fetchBlogArticles({}, forceRefresh),
      templatePage: () => fetchTemplatePage(forceRefresh),
      blogCategories: () => fetchBlogCategories(forceRefresh),
      templates: () => fetchTemplates({}, forceRefresh),
      professionCategories: () => fetchProfessionCategories(forceRefresh),
      all: () => {
        Object.keys(refreshMap).forEach(key => {
          if (key !== 'all' && refreshMap[key]) {
            refreshMap[key]();
          }
        });
      },
    };
    
    if (dataType && refreshMap[dataType]) {
      return refreshMap[dataType]();
    }
    
    log.warn(`Unknown data type: ${dataType}`);
    return Promise.resolve();
  }, [
    fetchHomePage,
    fetchAboutPage,
    fetchTemplatePage,
    fetchContactPage,
    fetchProfessionPage,
    fetchPrivacyPage,
    fetchSiteConfig,
    fetchBlogArticles,
    fetchBlogCategories,
    fetchTemplates,
    fetchProfessionCategories,
  ]);
  
  // Manual data update
  const updateData = useCallback((key, newData) => {
    setData(prev => ({ ...prev, [key]: newData }));
    invalidateCache(key);
  }, [invalidateCache]);
  
  // Clear specific error
  const clearError = useCallback((key) => {
    setErrors(prev => ({ ...prev, [key]: null }));
  }, []);
  
  // Clear all errors
  const clearAllErrors = useCallback(() => {
    setErrors({});
  }, []);
  
  // Get loading status for multiple keys
  const getLoadingStatus = useCallback((keys) => {
    if (!keys || !Array.isArray(keys)) return false;
    return keys.some(key => loading[key]);
  }, [loading]);
  
  // Get error status for multiple keys
  const getErrorStatus = useCallback((keys) => {
    if (!keys || !Array.isArray(keys)) return null;
    const errorKeys = keys.filter(key => errors[key]);
    return errorKeys.length > 0 ? errorKeys[0] : null;
  }, [errors]);
  
  // Context value
  const value = useMemo(() => ({
    // Data
    data,
    loading,
    errors,
    apiConnected,
    
    // Actions
    refreshData,
    updateData,
    clearError,
    clearAllErrors,
    getLoadingStatus,
    getErrorStatus,
    
    // Direct fetch functions
    fetchHomePage,
    fetchAboutPage,
    fetchContactPage,
    fetchProfessionPage,
    fetchPrivacyPage,
    fetchSiteConfig,
    fetchBlogArticles,
    fetchBlogCategories,
    fetchTemplates,
    fetchProfessionCategories,
    
    // Special blog functions
    fetchFeaturedArticles,
    fetchTrendingArticles,
    fetchLatestArticles,
    fetchBlogArticleBySlug,
    fetchTemplateBySlug,
    
    // Invalidate cache
    invalidateCache: (key) => {
      invalidateCache(key);
      log.info(`🗑️ Cache invalidated for: ${key || 'all'}`);
    },
    
    // Clear all caches
    clearAllCaches: () => {
      clearAllCaches();
      setData({
        homePage: null,
        aboutPage: null,
        contactPage: null,
        professionPage: null,
        privacyPage: null,
        siteConfig: null,
        blogArticles: [],
        blogCategories: [],
        templates: [],
        professionCategories: [],
      });
    },
    
    // Debug function to check current state
    debugState: () => {
      log.info('=== DEBUG DATA STATE ===');
      Object.keys(data).forEach(key => {
        const value = data[key];
        if (Array.isArray(value)) {
          log.info(`${key}: ${value.length} items`);
        } else if (value) {
          log.info(`${key}: Loaded`);
        } else {
          log.info(`${key}: Not loaded`);
        }
      });
      log.info('=== END DEBUG ===');
    },
  }), [
    data,
    loading,
    errors,
    apiConnected,
    refreshData,
    updateData,
    clearError,
    clearAllErrors,
    getLoadingStatus,
    getErrorStatus,
    fetchHomePage,
    fetchAboutPage,
    fetchContactPage,
    fetchProfessionPage,
    fetchPrivacyPage,
    fetchSiteConfig,
    fetchBlogArticles,
    fetchBlogCategories,
    fetchTemplates,
    fetchProfessionCategories,
    fetchFeaturedArticles,
    fetchTrendingArticles,
    fetchLatestArticles,
    fetchBlogArticleBySlug,
    fetchTemplateBySlug,
    invalidateCache,
  ]);
  
  return (
    <DataContext.Provider value={value}>
      {children}
    </DataContext.Provider>
  );
};

DataProvider.propTypes = {
  children: PropTypes.node.isRequired,
};

// Custom hook for page data
export const usePageData = (pageType) => {
  const { data, loading, errors, refreshData } = useData();
  
  return {
    data: data[pageType],
    isLoading: loading[pageType],
    error: errors[pageType],
    refresh: () => refreshData(pageType),
  };
};

// Custom hook for SEO data
export const useSEO = (pageType, customSEO = {}) => {
  const { data } = useData();
  const pageData = data[pageType];
  
  const getSEOData = () => {
    if (!pageData) return customSEO;
    
    return {
      meta_title: pageData.meta_title || customSEO.meta_title,
      meta_description: pageData.meta_description || customSEO.meta_description,
      meta_keywords: pageData.meta_keywords || customSEO.meta_keywords,
      og_title: pageData.og_title || customSEO.og_title,
      og_description: pageData.og_description || customSEO.og_description,
      og_image: pageData.og_image ? getMediaUrl(pageData.og_image) : customSEO.og_image,
      twitter_title: pageData.twitter_title || customSEO.twitter_title,
      twitter_description: pageData.twitter_description || customSEO.twitter_description,
      twitter_image: pageData.twitter_image ? getMediaUrl(pageData.twitter_image) : customSEO.twitter_image,
      canonical_url: pageData.canonical_url || customSEO.canonical_url,
      robots_txt: pageData.robots_txt || customSEO.robots_txt,
    };
  };
  
  return getSEOData();
};

// Custom hook for blog data
export const useBlogData = () => {
  const { 
    data, 
    loading, 
    errors, 
    fetchBlogArticles, 
    fetchBlogCategories,
    fetchFeaturedArticles,
    fetchTrendingArticles,
    fetchLatestArticles,
    fetchBlogArticleBySlug,
    refreshData 
  } = useData();
  
  return {
    // Data
    articles: Array.isArray(data.blogArticles) ? data.blogArticles : [],
    categories: Array.isArray(data.blogCategories) ? data.blogCategories : [],
    
    // Loading states
    isLoading: loading.blogArticles || loading.blogCategories,
    isLoadingArticles: loading.blogArticles,
    isLoadingCategories: loading.blogCategories,
    
    // Errors
    error: errors.blogArticles || errors.blogCategories,
    articlesError: errors.blogArticles,
    categoriesError: errors.blogCategories,
    
    // Actions
    refreshArticles: (params) => fetchBlogArticles(params, true),
    refreshCategories: () => refreshData('blogCategories', true),
    
    // Special fetch functions
    getFeaturedArticles: fetchFeaturedArticles,
    getTrendingArticles: fetchTrendingArticles,
    getLatestArticles: fetchLatestArticles,
    getArticleBySlug: fetchBlogArticleBySlug,
  };
};

// Custom hook for templates
export const useTemplates = () => {
  const { 
    data, 
    loading, 
    errors, 
    fetchTemplates,
    fetchTemplateBySlug,
    refreshData 
  } = useData();
  
  return {
    // Data
    templates: Array.isArray(data.templates) ? data.templates : [],
    
    // Loading states
    isLoading: loading.templates,
    
    // Errors
    error: errors.templates,
    
    // Actions
    refreshTemplates: (params) => fetchTemplates(params, true),
    getTemplateBySlug: fetchTemplateBySlug,
  };
};

export default {
  DataProvider,
  useData,
  usePageData,
  useSEO,
  useBlogData,
  useTemplates,
};